import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  downloadFinalCompletedSubmissionRequest,
  downloadOriginFilesRequest,
  getAllTemplates,
  getAuditLog,
  getEventLog,
  getPartyData,
  getSubmissions,
  getSubmissionsStatusSeeder,
  getToolsFromTemplate,
  getVerificationMethods,
  removeSubmission,
  triggerBrowserDownload,
  updateSubmissionPartiesRequest,
  updateSubmissionRecipientsRequest,
} from "../../../services/api/submissions/submissionsApi";

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;

  if (responseData?.errors && typeof responseData.errors === "object") {
    const firstErrorGroup = Object.values(responseData.errors)[0];
    if (Array.isArray(firstErrorGroup) && firstErrorGroup.length > 0) {
      return firstErrorGroup[0];
    }
  }

  if (
    typeof responseData?.message === "string" &&
    responseData.message.trim()
  ) {
    return responseData.message;
  }

  return fallbackMessage;
};

export const fetchSubmissions = createAsyncThunk(
  "submissions/fetchSubmissions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getSubmissions(params);
      const { submissions, status_counts } = response.data.data;

      return {
        submissions: submissions.data,
        status_counts,
        pagination: {
          current_page: submissions.current_page,
          last_page: submissions.last_page,
          total: submissions.total,
          per_page: submissions.per_page,
          next_page_url: submissions.next_page_url,
          last_page_url: submissions.last_page_url,
        },
      };
    } catch (error) {
      return rejectWithValue("Failed to fetch submissions");
    }
  },
);

export const fetchSubmissionsStatusSeeder = createAsyncThunk(
  "submissions/fetchSubmissionsStatusSeeder",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSubmissionsStatusSeeder();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch submissions status seeder");
    }
  },
);

export const fetchToolsFromTemplate = createAsyncThunk(
  "submissions/fetchToolsFromTemplate",
  // arg: a template id, or { templateId, submissionId } to load that submission's data
  async (arg, { rejectWithValue }) => {
    try {
      const { templateId, submissionId } =
        arg && typeof arg === "object" ? arg : { templateId: arg };
      const response = await getToolsFromTemplate(templateId, submissionId);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch template tools");
    }
  },
);

export const fetchAllTemplates = createAsyncThunk(
  "submissions/fetchAllTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllTemplates();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch templates");
    }
  },
);

export const downloadFinalCompletedSubmission = createAsyncThunk(
  "submissions/downloadFinalCompletedSubmission",
  async (sub_id, { rejectWithValue }) => {
    try {
      const response = await downloadFinalCompletedSubmissionRequest(sub_id);
      const downloadUrl = response.data?.download_url;

      if (!downloadUrl) {
        throw new Error("Download URL was not returned by the API.");
      }

      triggerBrowserDownload(downloadUrl, `submission_${sub_id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to download submission"),
      );
    }
  },
);

export const downloadOriginFiles = createAsyncThunk(
  "submissions/downloadOriginFiles",
  async (subId, { rejectWithValue }) => {
    try {
      const response = await downloadOriginFilesRequest(subId);
      const downloadUrl =
        response.data?.download_url || response.data?.data?.download_url;

      if (!downloadUrl) {
        throw new Error("Download URL was not returned by the API.");
      }

      triggerBrowserDownload(downloadUrl, "origin_files.zip");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to download origin files."),
      );
    }
  },
);

export const fetchPartyData = createAsyncThunk(
  "submissions/fetchPartyData",
  async (sub_id, { rejectWithValue }) => {
    try {
      const response = await getPartyData(sub_id);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch party data");
    }
  },
);

export const fetchAuditLog = createAsyncThunk(
  "submissions/fetchAuditLog",
  async (sub_id, { rejectWithValue }) => {
    try {
      const response = await getAuditLog(sub_id);
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch audit log");
    }
  },
);

export const fetchEventLog = createAsyncThunk(
  "submissions/fetchEventLog",
  async (sub_id, { rejectWithValue }) => {
    try {
      const response = await getEventLog(sub_id);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch event log");
    }
  },
);

export const deleteSubmission = createAsyncThunk(
  "submissions/deleteSubmission",
  async (submissionId, { rejectWithValue }) => {
    try {
      await removeSubmission(submissionId);
      return submissionId;
    } catch (error) {
      return rejectWithValue("Failed to delete submission");
    }
  },
);

export const updateSubmissionRecipients = createAsyncThunk(
  "submissions/updateSubmissionRecipients",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateSubmissionRecipientsRequest(payload);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update recipients");
    }
  },
);

export const fetchVerificationMethods = createAsyncThunk(
  "submissions/fetchVerificationMethods",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getVerificationMethods();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch verification methods");
    }
  },
);

export const updateSubmissionParties = createAsyncThunk(
  "submissions/updateSubmissionParties",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateSubmissionPartiesRequest(payload);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update parties");
    }
  },
);

const submissionsSlice = createSlice({
  name: "submissions",
  initialState: {
    submissionData: [],
    submissionCounts: [],
    currentPage: 1,
    lastPage: 1,
    totalSubmissions: 0,
    perPage: 10,
    nextPageUrl: null,
    lastPageUrl: null,
    loading: false,
    error: null,
    partyData: null,
    partyDataLoading: false,
    partyDataError: null,
    auditLog: null,
    auditLogLoading: false,
    auditLogError: null,
    eventLog: null,
    eventLogLoading: false,
    eventLogError: null,
    verificationMethods: [],
    verificationMethodsLoading: false,
    verificationMethodsError: null,
    submissionStatusSeeder: [],
    submissionStatusSeederLoading: false,
    submissionStatusSeederError: null,
    submissionTemplateTools: [],
    submissionTemplateToolsLoading: false,
    submissionTemplateToolsError: null,
    submissionTemplates: [],
    submissionTemplatesLoading: false,
    submissionTemplatesError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPartyData.pending, (state) => {
        state.partyDataLoading = true;
        state.partyDataError = null;
      })
      .addCase(fetchPartyData.fulfilled, (state, action) => {
        state.partyDataLoading = false;
        state.partyData = action.payload;
      })
      .addCase(fetchPartyData.rejected, (state, action) => {
        state.partyDataLoading = false;
        state.partyDataError = action.payload;
      })
      .addCase(fetchAuditLog.pending, (state) => {
        state.auditLogLoading = true;
        state.auditLogError = null;
      })
      .addCase(fetchAuditLog.fulfilled, (state, action) => {
        state.auditLogLoading = false;
        state.auditLog = action.payload;
      })
      .addCase(fetchAuditLog.rejected, (state, action) => {
        state.auditLogLoading = false;
        state.auditLogError = action.payload;
      })
      .addCase(fetchEventLog.pending, (state) => {
        state.eventLogLoading = true;
        state.eventLogError = null;
      })
      .addCase(fetchEventLog.fulfilled, (state, action) => {
        state.eventLogLoading = false;
        state.eventLog = action.payload;
      })
      .addCase(fetchEventLog.rejected, (state, action) => {
        state.eventLogLoading = false;
        state.eventLogError = action.payload;
      })
      .addCase(fetchSubmissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubmissions.fulfilled, (state, action) => {
        state.loading = false;
        state.submissionData = action.payload.submissions;
        state.submissionCounts = action.payload.status_counts;
        state.currentPage = action.payload.pagination.current_page;
        state.lastPage = action.payload.pagination.last_page;
        state.totalSubmissions = action.payload.pagination.total;
        state.perPage = action.payload.pagination.per_page || state.perPage;
        state.nextPageUrl = action.payload.pagination.next_page_url || null;
        state.lastPageUrl = action.payload.pagination.last_page_url || null;
      })
      .addCase(fetchSubmissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSubmission.fulfilled, (state, action) => {
        state.submissionData = state.submissionData.filter(
          (submission) => submission.id !== action.payload,
        );
      })
      .addCase(deleteSubmission.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateSubmissionRecipients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubmissionRecipients.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateSubmissionRecipients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchVerificationMethods.pending, (state) => {
        state.verificationMethodsLoading = true;
        state.verificationMethodsError = null;
      })
      .addCase(fetchVerificationMethods.fulfilled, (state, action) => {
        state.verificationMethodsLoading = false;
        state.verificationMethods = action.payload;
      })
      .addCase(fetchVerificationMethods.rejected, (state, action) => {
        state.verificationMethodsLoading = false;
        state.verificationMethodsError = action.payload;
      })
      .addCase(fetchSubmissionsStatusSeeder.pending, (state) => {
        state.submissionStatusSeederLoading = true;
        state.submissionStatusSeederError = null;
      })
      .addCase(fetchSubmissionsStatusSeeder.fulfilled, (state, action) => {
        state.submissionStatusSeederLoading = false;
        state.submissionStatusSeeder = action.payload;
      })
      .addCase(fetchSubmissionsStatusSeeder.rejected, (state, action) => {
        state.submissionStatusSeederLoading = false;
        state.submissionStatusSeederError = action.payload;
      })
      .addCase(fetchToolsFromTemplate.pending, (state) => {
        state.submissionTemplateToolsLoading = true;
        state.submissionTemplateToolsError = null;
      })
      .addCase(fetchToolsFromTemplate.fulfilled, (state, action) => {
        state.submissionTemplateToolsLoading = false;
        state.submissionTemplateTools = action.payload;
      })
      .addCase(fetchToolsFromTemplate.rejected, (state, action) => {
        state.submissionTemplateToolsLoading = false;
        state.submissionTemplateToolsError = action.payload;
      })
      .addCase(fetchAllTemplates.pending, (state) => {
        state.submissionTemplatesLoading = true;
        state.submissionTemplatesError = null;
      })
      .addCase(fetchAllTemplates.fulfilled, (state, action) => {
        state.submissionTemplatesLoading = false;
        state.submissionTemplates = action.payload;
      })
      .addCase(fetchAllTemplates.rejected, (state, action) => {
        state.submissionTemplatesLoading = false;
        state.submissionTemplatesError = action.payload;
      })
      .addCase(updateSubmissionParties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubmissionParties.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateSubmissionParties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default submissionsSlice.reducer;
